import React from 'react';
import {observer} from 'mobx-react';
import {stylesheet} from 'typestyle';
import PaperSheet from './PaperSheet';
import LeftSideBar from './LeftSideBar';
import {NumericFormatter} from '../../utility/FormatValueUtils';
import {Channel} from '../../types/Channel';
import {BuildingBlockType, ReportBuildingBlock} from '../types/Report';
import {ReportEditorState, SaveConflict} from '../stores/ReportEditorStore';
import PreviewPaperSheet from './PreviewPaperSheet';
import {BlackColorFaded20, LightGreyColor, WhiteColor} from '../../styles/colors';
import SaveConflictModal from './SaveConflictModal';
import Store from '../../stores/store';

interface ReportEditorProps {
    store: Store;
    moneyFormatToTargetCurrency: (fractions?: number) => NumericFormatter;
    targetCurrency?: string;
    reportEditorStore: ReportEditorState;
    getFullChannelForChannelName: (channelName: string) => Channel;
}

@observer
export default class ReportEditor extends React.Component<ReportEditorProps> {
    private removeBuildingBlock = (idBuildingBlock: string) => {
        this.props.reportEditorStore.reportEditing.blocks = this.props.reportEditorStore.reportEditing.blocks.filter(
            (buildingBlock: ReportBuildingBlock) => buildingBlock.id !== idBuildingBlock
        );
    };

    private closeEditor = () => {
        this.props.reportEditorStore.closeReportEditor();
    };

    private changeReportName = (reportName: string) => {
        this.props.reportEditorStore.reportEditing.name = reportName;
    };

    private closeErrorBox = () => {
        this.props.reportEditorStore.reportEditing = {
            ...this.props.reportEditorStore.reportEditing,
            errors: [],
        };
    };

    private changeBuildingBlockType = (idBuildingBlock: string, newBlockType: BuildingBlockType) => {
        this.props.reportEditorStore.changeBuildingBlockType(idBuildingBlock, newBlockType);
    };

    public render() {
        const {reportEditorStore, targetCurrency, moneyFormatToTargetCurrency} = this.props;
        return (
            <div className={editorCss.outerContainer}>
                <div className={editorCss.innerContainer}>
                    <LeftSideBar
                        dateRangeAdderProps={{
                            namedDateRanges: reportEditorStore.reportEditing.namedDateRanges,
                            namedDateRangeActions: {
                                create: reportEditorStore.addNamedDateRange,
                                rename: reportEditorStore.renameNamedDateRange,
                                remove: reportEditorStore.removeNamedDateRange,
                                setPreviewDateRange: this.props.reportEditorStore.setPreviewDateRange,
                            },
                        }}
                        reportName={reportEditorStore.reportEditing.name}
                        changeReportName={this.changeReportName}
                        closeEditor={this.closeEditor}
                        saveReport={reportEditorStore.saveReport}
                        isSaving={reportEditorStore.isSaving}
                        reportEditing={reportEditorStore.reportEditing}
                        closeErrorBox={this.closeErrorBox}
                        setPageLayout={reportEditorStore.setPageLayout}
                        previewModeEnabled={reportEditorStore.previewModeEnabled}
                        togglePreviewMode={reportEditorStore.togglePreviewMode}
                    />
                    <div className={editorCss.paperContainer}>
                        <div className={editorCss.pageCenterContainer}>
                            <div className={editorCss.pageCutoutContainer}>
                                {reportEditorStore.previewModeEnabled ? (
                                    <PreviewPaperSheet
                                        targetCurrency={targetCurrency}
                                        namedDateRanges={reportEditorStore.reportEditing.namedDateRanges}
                                        getMergedBuildingBlockInstructions={reportEditorStore.getMergedBuildingBlockInstructions}
                                        reportBuildingBlocks={reportEditorStore.reportEditing.blocks}
                                        moneyFormatToTargetCurrency={moneyFormatToTargetCurrency}
                                        getFullChannelForChannelName={this.props.getFullChannelForChannelName}
                                    />
                                ) : (
                                    <PaperSheet
                                        removeDiagramCompareTraitFromBuildingBlock={reportEditorStore.removeDiagramCompareTraitFromBuildingBlock}
                                        moneyFormatToTargetCurrency={moneyFormatToTargetCurrency}
                                        targetCurrency={targetCurrency}
                                        traitChecks={{
                                            reportBuildingBlocksAreEmpty: reportEditorStore.reportBuildingBlocksAreEmpty,
                                            hasSearchProductMixedBuildingBlocks: reportEditorStore.hasSearchProductMixedBuildingBlocks,
                                            hasToManyChannels: reportEditorStore.hasToManyChannels,
                                            hasToManySearches: reportEditorStore.hasToManySearches,
                                            hasToManySortings: reportEditorStore.hasToManySortings,
                                        }}
                                        allowBlockCombining={reportEditorStore.allowBlockCombining}
                                        checkIfIdBelongsToBuildingBlock={reportEditorStore.checkIfIdBelongsToBuildingBlock}
                                        changeSelectedNamedDateRange={reportEditorStore.changeSelectedNamedDateRange}
                                        namedDateRanges={reportEditorStore.reportEditing.namedDateRanges}
                                        changeBuildingBlockType={this.changeBuildingBlockType}
                                        updateTableColumnVisibility={reportEditorStore.updateTableColumnVisibility}
                                        updateLineLimitForTableBlock={reportEditorStore.updateLineLimitForTableBlock}
                                        getMergedBuildingBlockInstructions={reportEditorStore.getMergedBuildingBlockInstructions}
                                        reportBuildingBlocks={reportEditorStore.reportEditing.blocks}
                                        removeBuildingBlock={this.removeBuildingBlock}
                                        removeDiagramTraitFromBuildingBlock={reportEditorStore.removeDiagramTraitFromBuildingBlock}
                                        removeTableTraitFromBuildingBlock={reportEditorStore.removeTableTraitFromBuildingBlock}
                                        getFullChannelForChannelName={this.props.getFullChannelForChannelName}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                {reportEditorStore.saveConflict ? (
                    <SaveConflictModal
                        renameAndSaveAsNewAfterConflict={reportEditorStore.renameAndSaveAsNewAfterConflict}
                        reportName={reportEditorStore.reportEditing.name}
                        toRenameView={() => (this.props.reportEditorStore.saveConflict = {showRenameDialog: true, changedOnDisk: true})}
                        setSaveConflict={(saveConflict?: SaveConflict) => (reportEditorStore.saveConflict = saveConflict)}
                        saveConflict={reportEditorStore.saveConflict}
                        saveReport={reportEditorStore.saveReport}
                    />
                ) : null}
                <div className={editorCss.backdrop} />
            </div>
        );
    }
}

const editorCss = stylesheet({
    reportNameInput: {
        width: 200,
        padding: 5,
    },
    reportNameContainer: {
        position: 'absolute',
        top: 0,
        right: 0,
        padding: 10,
        backgroundColor: WhiteColor,
        border: '1px solid ' + LightGreyColor,
        borderBottomLeftRadius: 5,
    },
    pageCenterContainer: {
        display: 'flex',
        height: '100%',
        width: '100%',
        justifyContent: 'center',
    },
    pageCutoutContainer: {
        maxWidth: 1050,
        height: '100%',
        width: '100%',
    },
    paperContainer: {
        flex: 1,
        flexGrow: 1,
        display: 'flex',
        marginBottom: 23,
        marginTop: 38,
        marginLeft: 25,
        marginRight: 25,
        zIndex: 3,
    },
    innerContainer: {
        height: '100%',
        display: 'flex',
        flexDirection: 'row',
    },
    backdrop: {
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: BlackColorFaded20,
        zIndex: 2,
    },
    outerContainer: {
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
    },
});



// WEBPACK FOOTER //
// ./src/report/views/ReportEditor.tsx